<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted, type Component } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useMediaQuery } from "@vueuse/core";
import { useAuthStore } from "@/stores/auth";
import { useMaintenanceStore } from "@/stores/maintenance";
import { useTeamStore } from "@/stores/team";
import { usePrinterStore } from "@/stores/printer";
import { usePrintStore } from "@/stores/print";
import { useTheme } from "@/composables/useTheme";
import { useBridgeAccess } from "@/composables/useBridgeAccess";
import GlobalSearch from "@/components/layout/GlobalSearch.vue";
import SidebarStorage from "@/components/layout/SidebarStorage.vue";
import SidebarVault from "@/components/layout/SidebarVault.vue";
import SidebarKairo from "@/components/layout/SidebarKairo.vue";
import FeedbackDialog from "@/components/cloud/FeedbackDialog.vue";
import NotificationsMenu from "@/components/cloud/NotificationsMenu.vue";
import { useCloudStore } from "@/stores/cloud";
import { useFeedbackDialog } from "@/composables/useFeedbackDialog";

import { version as appVersion } from '../../../package.json'

import {
	DashboardSquare01Icon,
	Layers01Icon,
	PrinterIcon,
	Folder01Icon,
	CubeIcon,
	Settings01Icon,
	Logout01Icon,
	Moon02Icon,
	Sun03Icon,
	WrenchIcon,
	CloudIcon,
	Tag01Icon,
	CreditCardIcon,
	Crown02Icon,
	AiChat02Icon,
	PaintBrush01Icon,
	ArrowRight01Icon,
	ArrowDown01Icon,
	Building01Icon,
	UserGroupIcon,
	Calendar03Icon,
	SidebarLeftIcon,
	DiscordIcon,
	RedditIcon,
	BookOpen01Icon,
	BubbleChatIcon,
	ArrowReloadHorizontalIcon,
	LaptopIcon,
	DatabaseRestoreIcon,
	Share08Icon,
	UserMultipleIcon,
	Store01Icon,
	SecurityCheckIcon,
	SlidersHorizontalIcon,
	Home03Icon,
} from "@/components/icons";

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();
const maintenanceStore = useMaintenanceStore();
const teamStore = useTeamStore();
const printerStore = usePrinterStore();
const printStore = usePrintStore();
const { hasBridgeAccess } = useBridgeAccess();
const cloudStore = useCloudStore();
const feedback = useFeedbackDialog();
const cloudNav = computed(() => cloudStore.flag("cloud_dashboard"));

const { isDark, toggleTheme, initFromProfile } = useTheme();

const userMenuOpen = ref(false);
const userMenuRef = ref<HTMLElement | null>(null);

// ─── Sidebar ─────────────────────────────────────────────
// 1024px and up: the full 248px sidebar, collapsible to a 60px icon rail.
// 640–1023px: the icon rail, expanding over the page as a drawer.
// Below 640px: a top bar, with the sidebar as a drawer.
const SIDEBAR_KEY = "printhq-sidebar-collapsed";
const SIDEBAR_WIDTH = 248;
const RAIL_WIDTH = 60;
const sidebarCollapsed = ref(localStorage.getItem(SIDEBAR_KEY) === "true");
const drawerOpen = ref(false);
const isSm = useMediaQuery("(min-width: 640px)");
const isLg = useMediaQuery("(min-width: 1024px)");

const collapsed = computed(() => (isLg.value ? sidebarCollapsed.value : isSm.value && !drawerOpen.value));
const asideWidth = computed(() => (collapsed.value ? RAIL_WIDTH : SIDEBAR_WIDTH));
const mainOffset = computed(() => {
	if (isLg.value) return sidebarCollapsed.value ? RAIL_WIDTH : SIDEBAR_WIDTH;
	return isSm.value ? RAIL_WIDTH : 0;
});
const showScrim = computed(() => drawerOpen.value && !isLg.value);

const toggleSidebar = () => {
	if (isLg.value) {
		sidebarCollapsed.value = !sidebarCollapsed.value;
		localStorage.setItem(SIDEBAR_KEY, String(sidebarCollapsed.value));
	} else {
		drawerOpen.value = !drawerOpen.value;
	}
};
const sidebarToggleLabel = computed(() => {
	if (isLg.value) return sidebarCollapsed.value ? "Show sidebar" : "Hide sidebar";
	return drawerOpen.value ? "Close navigation" : "Open navigation";
});

const closeSidebar = () => {
	drawerOpen.value = false;
};

watch(() => route.fullPath, closeSidebar);
watch(isLg, closeSidebar);

function onWindowFocus() {
	cloudStore.onFocus();
}

onMounted(async () => {
	initFromProfile();
	teamStore.fetchTeams(false);
	document.addEventListener("click", handleClickOutside);
	document.addEventListener("keydown", handleKeydown);
	window.addEventListener("focus", onWindowFocus);
	// The account's plan, flags and notifications (PrintHQ Cloud).
	cloudStore.load().then(() => cloudStore.loadNotifications());

	// Global realtime subscriptions — prints & telemetry update everywhere
	// Only subscribe for bridge-tier users (hobby, maker, farm)
	if (authStore.userId && !authStore.isDemoMode) {
		await printerStore.fetchPrinters()
		await printStore.fetchPrints()
		if (hasBridgeAccess.value) {
			printerStore.subscribeTelemetry()
			printStore.subscribePrints()
		}
	}
});

onUnmounted(() => {
	document.removeEventListener("click", handleClickOutside);
	document.removeEventListener("keydown", handleKeydown);
	window.removeEventListener("focus", onWindowFocus);
	printerStore.unsubscribeTelemetry()
	printStore.unsubscribePrints()
});

function handleClickOutside(e: MouseEvent) {
	if (userMenuRef.value && !userMenuRef.value.contains(e.target as Node)) {
		userMenuOpen.value = false;
	}
}

function handleKeydown(e: KeyboardEvent) {
	if (e.key !== "Escape") return;
	userMenuOpen.value = false;
	drawerOpen.value = false;
}

// ─── Navigation Items ────────────────────────────────────
interface NavItem {
	name: string
	path: string
	icon: Component
	badge?: () => number
	desktopOnly?: boolean
	adminOnly?: boolean
	bridgeOnly?: boolean
	plannerOnly?: boolean
	kairoOnly?: boolean
	/** The previous AI Chat: hidden once Kairo is on. */
	beforeKairo?: boolean
}

const personalNavItems: NavItem[] = [
	{ name: "Dashboard", path: "/", icon: DashboardSquare01Icon },
	{ name: "Filaments", path: "/filaments", icon: Layers01Icon },
	{ name: "Printers", path: "/printers", icon: PrinterIcon },
	{ name: "Projects", path: "/projects", icon: Folder01Icon },
	{ name: "Prints", path: "/prints", icon: CubeIcon },
	{ name: "Maintenance", path: "/maintenance", icon: WrenchIcon, badge: () => maintenanceStore.overdueMaintenance.length },
	{ name: "Files", path: "/files", icon: CloudIcon },
	{ name: "Planner", path: "/planner", icon: Calendar03Icon, plannerOnly: true },
	{ name: "Scheduler", path: "/scheduler", icon: Calendar03Icon, bridgeOnly: true },
	{ name: "Painter", path: "/painter", icon: PaintBrush01Icon, desktopOnly: true, adminOnly: true },
	{ name: "Labels", path: "/labels", icon: Tag01Icon },
	{ name: "Kairo", path: "/kairo", icon: AiChat02Icon, kairoOnly: true },
	{ name: "AI Chat", path: "/chat", icon: AiChat02Icon, beforeKairo: true },
	{ name: "Affiliate", path: "/affiliate", icon: CreditCardIcon },
	{ name: "Locations", path: "/locations", icon: Building01Icon },
];

const teamNavItems = (teamId: string, userRole?: string): NavItem[] => {
	const base: NavItem[] = [
		{ name: "Dashboard", path: `/teams/${teamId}`, icon: DashboardSquare01Icon },
		{ name: "Printers", path: `/teams/${teamId}/printers`, icon: PrinterIcon },
		{ name: "Prints", path: `/teams/${teamId}/prints`, icon: CubeIcon },
		{ name: "Projects", path: `/teams/${teamId}/projects`, icon: Folder01Icon },
		{ name: "Filaments", path: `/teams/${teamId}/filaments`, icon: Layers01Icon },
		{ name: "Maintenance", path: `/teams/${teamId}/maintenance`, icon: WrenchIcon },
		{ name: "Files", path: `/teams/${teamId}/files`, icon: CloudIcon },
		{ name: "Scheduler", path: `/teams/${teamId}/scheduler`, icon: Calendar03Icon },
		{ name: "Locations", path: `/teams/${teamId}/locations`, icon: Building01Icon },
		{ name: "Members", path: `/teams/${teamId}/members`, icon: UserGroupIcon },
	]
	if (userRole === 'owner') {
		base.push({ name: "Settings", path: `/teams/${teamId}/settings`, icon: Settings01Icon })
	}
	return base
}

const filteredPersonalNav = computed(() =>
	personalNavItems.filter(item =>
		(!item.adminOnly || authStore.isAdmin) &&
		(!item.bridgeOnly || hasBridgeAccess.value) &&
		(!item.plannerOnly || cloudStore.flag("planner")) &&
		(!item.kairoOnly || cloudStore.flag("kairo")) &&
		(!item.beforeKairo || !cloudStore.flag("kairo")) &&
		(!item.desktopOnly || isLg.value)
	)
);

// PrintHQ Cloud (flag cloud_dashboard): grouped. Only modules that work are
// listed; the previous ones stay one click away under Workshop (legacy)
// while legacy_nav is on, so nothing anyone saved becomes unreachable.
interface NavGroup { title: string; items: NavItem[] }
const cloudGroups = computed<NavGroup[]>(() => {
	const groups: NavGroup[] = [
		{ title: "Cloud Workspace", items: [
			{ name: "Overview", path: "/", icon: Home03Icon },
			{ name: "Cloud Library", path: "/library", icon: CloudIcon },
			...(cloudStore.flag("planner") ? [{ name: "Planner", path: "/planner", icon: Calendar03Icon }] : []),
			...(cloudStore.flag("kairo") ? [{ name: "Kairo", path: "/kairo", icon: AiChat02Icon }] : []),
			{ name: "Sync Activity", path: "/sync", icon: ArrowReloadHorizontalIcon },
			{ name: "Devices", path: "/devices", icon: LaptopIcon },
			{ name: "Backups & Recovery", path: "/backups", icon: DatabaseRestoreIcon },
		] },
		{ title: "Sharing & Community", items: [
			{ name: "Shared Collections", path: "/shared", icon: Share08Icon },
			{ name: "Community", path: "/community", icon: UserMultipleIcon },
			{ name: "Storefronts", path: "/locations", icon: Store01Icon },
		] },
		{ title: "Account", items: [
			{ name: "Subscription & Billing", path: "/subscription", icon: CreditCardIcon },
			{ name: "Security", path: "/security", icon: SecurityCheckIcon },
			{ name: "Preferences", path: "/preferences", icon: SlidersHorizontalIcon },
		] },
	];
	if (cloudStore.flag("legacy_nav")) {
		groups.push({
			title: "Workshop (legacy)",
			items: filteredPersonalNav.value.filter((i) => !["/", "/locations", "/planner", "/kairo"].includes(i.path)),
		});
	}
	return groups;
});

const hasTeams = computed(() => authStore.isAdmin && teamStore.teams.length > 0);

// ─── Tab Switcher (Personal / Teams) ─────────────────────
const activeTab = ref<'personal' | 'teams'>('personal');

// Auto-switch tab based on current route
watch(() => route.path, (path) => {
	if (path.startsWith('/teams/')) {
		activeTab.value = 'teams'
		// Auto-expand the active team's group
		const match = path.match(/^\/teams\/([^/]+)/)
		if (match && teamGroupCollapsed.value[match[1]]) {
			teamGroupCollapsed.value[match[1]] = false
			persistTeamGroupState()
		}
	} else {
		activeTab.value = 'personal'
	}
}, { immediate: true });

// ─── Team Group Collapse State ───────────────────────────
const TEAM_GROUP_KEY = 'printhq-team-nav-collapsed'
const teamGroupCollapsed = ref<Record<string, boolean>>(
	JSON.parse(localStorage.getItem(TEAM_GROUP_KEY) || '{}')
)

const toggleTeamGroup = (teamId: string) => {
	teamGroupCollapsed.value[teamId] = !teamGroupCollapsed.value[teamId]
	persistTeamGroupState()
}

const persistTeamGroupState = () => {
	localStorage.setItem(TEAM_GROUP_KEY, JSON.stringify(teamGroupCollapsed.value))
}

// ─── Collapsed sidebar: detect which icons to show ───────
const collapsedTeamId = computed(() => {
	const match = route.path.match(/^\/teams\/([^/]+)/)
	return match ? match[1] : null
})

const collapsedNavItems = computed(() => {
	if (collapsedTeamId.value) {
		const team = teamStore.getById(collapsedTeamId.value)
		return teamNavItems(collapsedTeamId.value, team?.user_role)
	}
	return cloudNav.value ? cloudGroups.value.flatMap((g) => g.items) : filteredPersonalNav.value
})

const isActive = (path: string) => {
	if (path === "/") {
		return route.path === "/";
	}
	// Team dashboard: exact match only
	if (/^\/teams\/[^/]+$/.test(path)) {
		return route.path === path
	}
	return route.path.startsWith(path);
};

const formatCount = (n: number) => (n > 9999 ? "9999+" : n.toLocaleString());

const tierLabel = computed(() => {
	const e = cloudStore.entitlement;
	if (cloudNav.value && e) {
		if (e.state === 'trial_active') return 'Trial';
		if (e.entitled) return 'Maker';
	}
	const tier = authStore.subscriptionTier;
	if (tier === 'farm') return 'Farm';
	if (tier === 'commercial' || tier === 'maker') return 'Maker';
	if (tier === 'personal' || tier === 'hobby') return 'Hobby';
	return 'Free';
});

const initial = computed(() => authStore.displayName?.charAt(0).toUpperCase() || "?");

function navigateFromMenu(path: string) {
	userMenuOpen.value = false;
	router.push(path);
}

const handleSignOut = async () => {
	userMenuOpen.value = false;
	await authStore.signOut();
	router.push("/login");
};
</script>

<template>
	<div class="min-h-screen bg-bg text-fg">
		<!-- Drawer scrim -->
		<div v-if="showScrim" class="fixed inset-0 z-30 bg-black/40 backdrop-blur-[3px]" aria-hidden="true" @click="closeSidebar" />

		<!-- Sidebar -->
		<aside
			class="app-sidebar fixed inset-y-0 left-0 z-40 flex flex-col bg-sidebar border-r border-line transition-[width,transform] duration-150"
			:class="[!isSm && !drawerOpen ? '-translate-x-full' : 'translate-x-0', showScrim ? 'shadow-float' : '']"
			:style="{ width: `${asideWidth}px` }"
		>
			<!-- Logo -->
			<div class="h-12 shrink-0 flex items-center" :class="collapsed ? 'justify-center' : 'gap-2 px-4'">
				<RouterLink to="/" class="flex items-center rounded-md" aria-label="PrintHQ dashboard">
					<img v-if="collapsed" src="/favicon.png" alt="" class="size-9 object-contain" />
					<img v-else src="/logo-wordmark.svg" alt="PrintHQ" class="w-[104px] h-auto" />
				</RouterLink>
				<span v-if="!collapsed" class="ml-auto text-[11px] text-fg-3 tabular-nums" :title="`PrintHQ ${appVersion}`">v{{ appVersion }}</span>
			</div>

			<!-- Personal / Teams -->
			<div v-if="hasTeams && !collapsed" class="px-3 pb-2">
				<div class="segmented w-full" role="radiogroup" aria-label="Workspace">
					<button class="seg" role="radio" :aria-checked="activeTab === 'personal'" @click="activeTab = 'personal'">Personal</button>
					<button class="seg" role="radio" :aria-checked="activeTab === 'teams'" @click="activeTab = 'teams'">Teams</button>
				</div>
			</div>

			<!-- Navigation -->
			<nav class="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-3 py-1" aria-label="Navigation">
				<!-- Icon rail: labels move into tooltips, counts become dots -->
				<ul v-if="collapsed" class="flex flex-col gap-px">
					<li v-for="item in collapsedNavItems" :key="item.path">
						<RouterLink
							:to="item.path"
							class="nav-item relative justify-center px-0"
							:aria-current="isActive(item.path) ? 'page' : undefined"
							:aria-label="item.name"
							v-tooltip.right="item.name"
						>
							<component :is="item.icon" :size="17" />
							<span v-if="item.badge && item.badge() > 0" class="absolute top-1.5 right-2 size-2 rounded-full bg-warn" aria-hidden="true" />
						</RouterLink>
					</li>
				</ul>

				<template v-else>
					<!-- PrintHQ Cloud -->
					<div v-if="cloudNav" v-show="activeTab === 'personal'" class="flex flex-col gap-3">
						<section v-for="g in cloudGroups" :key="g.title" :aria-label="g.title">
							<p class="section-title h-6 px-2 flex items-center">{{ g.title }}</p>
							<ul class="flex flex-col gap-px">
								<li v-for="item in g.items" :key="item.path">
									<RouterLink :to="item.path" class="nav-item" :aria-current="isActive(item.path) ? 'page' : undefined">
										<component :is="item.icon" :size="17" />
										<span class="flex-1 min-w-0 truncate">{{ item.name }}</span>
										<span v-if="item.badge && item.badge() > 0" class="count warn" :aria-label="`${item.badge()} overdue`">{{ formatCount(item.badge()) }}</span>
									</RouterLink>
								</li>
							</ul>
						</section>
					</div>
					<!-- Personal -->
					<ul v-else v-show="activeTab === 'personal'" class="flex flex-col gap-px">
						<li v-for="item in filteredPersonalNav" :key="item.path">
							<RouterLink :to="item.path" class="nav-item" :aria-current="isActive(item.path) ? 'page' : undefined">
								<component :is="item.icon" :size="17" />
								<span class="flex-1 min-w-0 truncate">{{ item.name }}</span>
								<span
									v-if="item.badge && item.badge() > 0"
									class="count warn"
									:aria-label="`${item.badge()} overdue`"
								>{{ formatCount(item.badge()) }}</span>
							</RouterLink>
						</li>
					</ul>

					<!-- Teams -->
					<div v-show="activeTab === 'teams'" class="flex flex-col gap-3">
						<section v-for="team in teamStore.teams" :key="team.id" :aria-labelledby="`team-nav-${team.id}`">
							<button
								:id="`team-nav-${team.id}`"
								class="section-title w-full flex items-center gap-1 h-6 px-2 rounded-md hover:text-fg-2 transition-colors"
								:aria-expanded="!teamGroupCollapsed[team.id]"
								@click="toggleTeamGroup(team.id)"
							>
								<ArrowRight01Icon :size="12" class="transition-transform duration-150" :class="{ 'rotate-90': !teamGroupCollapsed[team.id] }" />
								<span class="truncate">{{ team.name }}</span>
							</button>
							<ul v-show="!teamGroupCollapsed[team.id]" class="flex flex-col gap-px mt-px">
								<li v-for="item in teamNavItems(team.id, team.user_role)" :key="item.path">
									<RouterLink :to="item.path" class="nav-item" :aria-current="isActive(item.path) ? 'page' : undefined">
										<component :is="item.icon" :size="17" />
										<span class="flex-1 min-w-0 truncate">{{ item.name }}</span>
									</RouterLink>
								</li>
							</ul>
						</section>

						<RouterLink to="/teams" class="nav-item" :aria-current="route.path === '/teams' ? 'page' : undefined">
							<Settings01Icon :size="17" />
							<span class="flex-1 min-w-0 truncate">Manage Teams</span>
						</RouterLink>
					</div>
				</template>
			</nav>

			<!-- Kairo -->
			<SidebarKairo v-if="cloudStore.flag('kairo')" :collapsed="collapsed" />

			<!-- PrintHQ Vault -->
			<SidebarVault :collapsed="collapsed" />

			<!-- Storage -->
			<SidebarStorage :collapsed="collapsed" />

			<!-- Docs and community -->
			<div class="px-3 pb-2 gap-1" :class="collapsed ? 'flex flex-col items-center' : 'grid grid-cols-2'">
				<a
					href="https://printhq.io/docs/"
					target="_blank"
					rel="noopener noreferrer"
					class="btn-ghost btn-sm"
					:class="collapsed ? 'size-8 px-0' : 'flex-1'"
					:aria-label="collapsed ? 'Docs' : undefined"
					v-tooltip.right="collapsed ? 'Docs' : undefined"
				>
					<BookOpen01Icon :size="14" />
					<span v-if="!collapsed">Docs</span>
				</a>
				<a
					href="https://discord.gg/XejG3QaS5v"
					target="_blank"
					rel="noopener noreferrer"
					class="btn-ghost btn-sm"
					:class="collapsed ? 'size-8 px-0' : 'flex-1'"
					:aria-label="collapsed ? 'Discord' : undefined"
					v-tooltip.right="collapsed ? 'Discord' : undefined"
				>
					<DiscordIcon :size="14" />
					<span v-if="!collapsed">Discord</span>
				</a>
				<button
					type="button"
					class="btn-ghost btn-sm"
					:class="collapsed ? 'size-8 px-0' : 'flex-1'"
					:aria-label="collapsed ? 'Give Feedback' : undefined"
					v-tooltip.right="collapsed ? 'Give Feedback' : undefined"
					@click="feedback.show()"
				>
					<BubbleChatIcon :size="14" />
					<span v-if="!collapsed">Feedback</span>
				</button>
				<a
					href="https://www.reddit.com/r/PrintHQ/"
					target="_blank"
					rel="noopener noreferrer"
					class="btn-ghost btn-sm"
					:class="collapsed ? 'size-8 px-0' : 'flex-1'"
					:aria-label="collapsed ? 'Reddit' : undefined"
					v-tooltip.right="collapsed ? 'Reddit' : undefined"
				>
					<RedditIcon :size="14" />
					<span v-if="!collapsed">Reddit</span>
				</a>
			</div>

			<!-- Account -->
			<div ref="userMenuRef" class="relative border-t border-line p-2">
				<button
					class="w-full flex items-center gap-2.5 h-9 rounded-lg hover:bg-card-hover transition-colors text-left"
					:class="collapsed ? 'justify-center' : 'px-1.5'"
					:aria-label="collapsed ? 'Account' : undefined"
					aria-haspopup="menu"
					:aria-expanded="userMenuOpen"
					@click="userMenuOpen = !userMenuOpen"
				>
					<img
						v-if="authStore.avatarUrl"
						:src="authStore.avatarUrl"
						alt=""
						class="size-6 rounded-full object-cover shrink-0"
					/>
					<span v-else class="size-6 rounded-full bg-accent-soft text-accent-text text-[11px] font-semibold flex items-center justify-center shrink-0">{{ initial }}</span>
					<template v-if="!collapsed">
						<span class="flex-1 min-w-0 truncate text-[13px] font-medium">{{ authStore.displayName }}</span>
						<span class="chip">{{ tierLabel }}</span>
						<ArrowDown01Icon :size="14" class="text-fg-3 transition-transform duration-150" :class="{ 'rotate-180': userMenuOpen }" />
					</template>
				</button>

				<div v-if="userMenuOpen" class="menu absolute bottom-full left-2 mb-1 w-64 z-50" role="menu" aria-label="Account">
					<div class="px-2.5 py-2">
						<p class="text-[13px] font-medium truncate">{{ authStore.displayName }}</p>
						<p class="text-xs text-fg-3 truncate">{{ authStore.profile?.email || authStore.user?.email }}</p>
					</div>
					<div class="menu-sep" />
					<button class="menu-item" role="menuitem" @click="navigateFromMenu('/subscription')">
						<CreditCardIcon />
						{{ cloudNav ? 'Subscription & Billing' : 'Subscription' }}
					</button>
					<template v-if="cloudNav">
						<button class="menu-item" role="menuitem" @click="navigateFromMenu('/security')">
							<SecurityCheckIcon />
							Security
						</button>
						<button class="menu-item" role="menuitem" @click="navigateFromMenu('/preferences')">
							<SlidersHorizontalIcon />
							Preferences
						</button>
					</template>
					<button class="menu-item" role="menuitem" @click="navigateFromMenu('/settings')">
						<Settings01Icon />
						Settings
					</button>
					<button class="menu-item" role="menuitem" @click="userMenuOpen = false; feedback.show()">
						<BubbleChatIcon />
						Give Feedback
					</button>
					<template v-if="authStore.isAdmin">
						<div class="menu-sep" />
						<button class="menu-item" role="menuitem" @click="navigateFromMenu('/admin')">
							<Crown02Icon />
							Admin Panel
						</button>
					</template>
					<div class="menu-sep" />
					<button class="menu-item is-danger" role="menuitem" @click="handleSignOut">
						<Logout01Icon />
						Sign Out
					</button>
				</div>
			</div>
		</aside>

		<!-- View -->
		<div class="app-main transition-[margin] duration-150" :style="{ marginLeft: `${mainOffset}px` }">
			<header class="app-toolbar sticky top-0 z-20 h-12 flex items-center gap-2 px-4 bg-bg border-b border-line">
				<button
					class="icon-btn -ml-1.5"
					:aria-label="sidebarToggleLabel"
					:title="sidebarToggleLabel"
					:aria-expanded="isLg ? !sidebarCollapsed : drawerOpen"
					@click="toggleSidebar"
				>
					<SidebarLeftIcon :size="17" />
				</button>

				<RouterLink v-if="!isSm" to="/" class="flex items-center rounded-md" aria-label="PrintHQ dashboard">
					<img src="/logo-wordmark.svg" alt="PrintHQ" class="w-[84px] h-auto" />
				</RouterLink>

				<GlobalSearch />

				<span class="flex-1" />

				<NotificationsMenu v-if="cloudNav" />
				<button
					class="icon-btn"
					:aria-label="isDark ? 'Switch to light theme' : 'Switch to dark theme'"
					:title="isDark ? 'Light theme' : 'Dark theme'"
					@click="toggleTheme"
				>
					<Sun03Icon v-if="isDark" />
					<Moon02Icon v-else />
				</button>
			</header>

			<main class="p-4 sm:p-6">
				<slot />
			</main>
		</div>

		<FeedbackDialog />
	</div>
</template>
